import {
  BoxV2 as Box,
  Button,
  DropdownV2 as Dropdown,
  Flex,
  MessageBar,
  TextInput,
  useHasPermission,
} from 'portal-commons';
import { FunctionComponent, useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Dialog, makeStyles } from '@material-ui/core';
import { faCircleUser, faInfo } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { userCreateRoleTooltipTemplate } from '../constants';
import { Role } from '@/types/user';
import { User, UserUpdatePayload } from '../types';
import { isRequired } from '../validators';
import { CustomTooltip } from '@/shared_elements';
import { roleEnumToLabel, toggleRole } from '../utils';
import { Checkbox } from '@/components';

interface UserEditModalProps {
  open: boolean;
  disabled: boolean;
  user: User;
  roles: string[];
  secondaryRoles: string[];
  onClose: () => void;
  onSubmit: (data: UserUpdatePayload) => void;
}

const useStyles = makeStyles({
  container: {
    width: '525px',
    padding: '24px 32px 9px',
  },
});

const UserEditModal: FunctionComponent<UserEditModalProps> = ({
  open,
  disabled,
  user,
  roles,
  secondaryRoles,
  onClose,
  onSubmit,
}) => {
  const classes = useStyles();
  const canAssignFinance = useHasPermission('profile.assignFinance') as boolean;

  // Pre-calculate default values based on the incoming user roles and valid role lists
  const defaultFormValues = useMemo(() => {
    const appRoles = user.profile.csp_app_role || [];
    return {
      userAppRole: appRoles.find((r) => roles.includes(r)) ?? Role.Viewer,
      secondaryUserAppRoles: appRoles.filter((r) => secondaryRoles.includes(r)),
    };
  }, [user.profile.csp_app_role, roles, secondaryRoles]);

  const { handleSubmit, control, reset } = useForm<UserUpdatePayload>({
    defaultValues: defaultFormValues,
  });

  const onFormSubmit = (data: UserUpdatePayload) => {
    const originalAppRoles = user.profile.csp_app_role || [];
    // Preserve roles that are not managed by the current UI (e.g., hidden or future roles)
    const unhandledRoles = originalAppRoles.filter(
      (role) => !roles.includes(role) && !secondaryRoles.includes(role)
    );

    onSubmit({
      ...data,
      secondaryUserAppRoles: [...data.secondaryUserAppRoles, ...unhandledRoles],
    });
  };

  const options = useMemo(
    () =>
      roles.map((role) => ({
        label: roleEnumToLabel(role),
        value: role,
      })),
    [roles]
  );

  useEffect(() => {
    if (open) {
      reset(defaultFormValues);
    }
  }, [open, defaultFormValues, reset]);

  return (
    <Dialog open={open} classes={{ paper: classes.container }}>
      <form noValidate onSubmit={handleSubmit(onFormSubmit)}>
        <Flex
          sx={{
            flexDirection: 'column',
            rowGap: '8px',
            color: '#333E41',
            fontFamily: 'Roboto',
          }}
          data-testid="userEditModal"
        >
          <Flex
            sx={{
              flexDirection: 'row',
              justifyContent: 'center',
              columnGap: '12px',
              padding: '12px 0',
              fontWeight: 600,
              fontSize: '24px',
              lineHeight: '28px',
            }}
          >
            <FontAwesomeIcon icon={faCircleUser} />
            <Box>Edit User</Box>
          </Flex>
          <Box
            sx={{
              padding: '8px 0 12px',
              textAlign: 'center',
              fontWeight: 500,
              fontSize: '16px',
              lineHeight: '19px',
            }}
          >
            USER ID: <span data-testid="id">{user.id}</span>
          </Box>
          <Box
            sx={{
              fontWeight: 400,
              fontSize: '10px',
              lineHeight: '12px',
            }}
          >
            * Indicates a Required Field
          </Box>
          <Flex sx={{ flexDirection: 'column', rowGap: 20 }}>
            <TextInput
              readOnly
              label="First Name"
              value={user.profile.firstName}
              data-testid="firstName"
            />
            <TextInput
              readOnly
              label="Last Name"
              value={user.profile.lastName}
              data-testid="lastName"
            />
            <TextInput
              readOnly
              label="Email"
              value={user.profile.email}
              data-testid="email"
            />
            <Flex
              sx={{
                flexDirection: 'row',
                alignItems: 'flex-start',
                columnGap: '8px',
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Controller
                  name="userAppRole"
                  control={control}
                  rules={{
                    validate: {
                      isRequired,
                    },
                  }}
                  render={({
                    field: { value, onChange, onBlur },
                    fieldState: { error = {} },
                  }) => (
                    <Dropdown
                      required
                      disabled={disabled}
                      error={!!error.message}
                      label="Role"
                      helperText={error.message}
                      options={options}
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      data-testid="role"
                    />
                  )}
                />
              </Box>
              <Flex
                sx={{
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '40px',
                }}
              >
                <CustomTooltip
                  arrow
                  placement="right"
                  title={userCreateRoleTooltipTemplate}
                  style={{ color: '#0091B3' }}
                >
                  <FontAwesomeIcon icon={faInfo} />
                </CustomTooltip>
              </Flex>
            </Flex>
          </Flex>
          <Box
            sx={{ height: 1, background: '#CCCFD0', width: '100%', my: 16 }}
          />
          <Flex sx={{ flexDirection: 'column', gap: 8, mb: 8 }}>
            <Box sx={{ padding: '7px 4px' }}>
              <Controller
                name="secondaryUserAppRoles"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Checkbox
                    label="Finance Permission"
                    disabled={!canAssignFinance}
                    checked={(value || []).includes(Role.Finance)}
                    onChange={(checked: boolean) =>
                      onChange(toggleRole(value || [], Role.Finance, checked))
                    }
                    data-testid="financePermissionCheckbox"
                  />
                )}
              />
            </Box>
            <Flex>
              <Box sx={{ width: 28 }} />
              <MessageBar
                type="info"
                title="Grants a user access to view and download detailed billing records. Only users with a Manager role can assign this permission."
                showClose={false}
                sx={{
                  '& h4': {
                    fontWeight: 400,
                    color: '#4D5659',
                  },
                }}
              />
            </Flex>
          </Flex>
          <Flex
            sx={{
              flexDirection: 'row',
              justifyContent: 'center',
              columnGap: '20px',
              padding: '24px 0 40px',
            }}
          >
            <Button
              variant="outline"
              onClick={onClose}
              data-testid="cancelButton"
            >
              Cancel
            </Button>
            <Button type="submit" disabled={disabled} data-testid="saveButton">
              Save
            </Button>
          </Flex>
        </Flex>
      </form>
    </Dialog>
  );
};

export default UserEditModal;
